import type { Objetivo, Step } from '@/types'
import { baseSteps } from '../shared/base'
import { endSteps } from '../shared/end'
import { celluliteSteps } from './cellulite'
import { energyPerformanceSteps } from './energy_performance'
import { focusCognitionSteps } from './focus_cognition'
import { hairLossSteps } from './hair_loss'
import { hairSkinNailsSteps } from './hair_skin_nails'
import { immunitySteps } from './immunity'
import { lipedemaSteps } from './lipedema'
import { menopauseSteps } from './menopause'
import { sexualHealthSteps } from './sexual_health'
import { skinCareSteps } from './skin_care'
import { sleepSteps } from './sleep'
import { weightLossSteps } from './weight_loss'

const objectiveSteps: Record<Objetivo, Step[]> = {
  genetics: [],
  cabelos_pele_unhas: hairSkinNailsSteps,
  melasma: skinCareSteps,
  calvicie: hairLossSteps,
  emagrecimento: weightLossSteps,
  lipedema: lipedemaSteps,
  celulite: celluliteSteps,
  disfuncao_eretil: sexualHealthSteps,
  desempenho_fisico: energyPerformanceSteps,
  sono: sleepSteps,
  foco_cognicao: focusCognitionSteps,
  imunidade: immunitySteps,
  menopausa: menopauseSteps,
  libido: [],
  fertilidade: [],
}

type ThemeKey =
  | 'metas_contexto'
  | 'sinais_queixas'
  | 'historico_clinico'
  | 'habitos_estilo_vida'
  | 'complementares'

const themeOrder: ThemeKey[] = [
  'metas_contexto',
  'sinais_queixas',
  'historico_clinico',
  'habitos_estilo_vida',
  'complementares',
]

const themeTransitionMessages: Record<ThemeKey, string> = {
  metas_contexto: 'Vamos alinhar seus objetivos e contexto.',
  sinais_queixas: 'Agora vamos entender seus sinais e queixas principais.',
  historico_clinico: 'Em seguida, vamos revisar seu histórico de saúde e tratamentos.',
  habitos_estilo_vida: 'Agora vamos mapear sua rotina e estilo de vida.',
  complementares: 'Só mais algumas perguntas complementares para finalizar.',
}

const introThemeMap: Record<string, ThemeKey> = {
  intro_general: 'metas_contexto',
  intro_weight_loss: 'metas_contexto',
  intro_energy_performance: 'metas_contexto',
  intro_hair: 'sinais_queixas',
  intro_face: 'sinais_queixas',
  intro_body: 'sinais_queixas',
  intro_lipedema: 'sinais_queixas',
  intro_sleep: 'sinais_queixas',
  intro_nails: 'sinais_queixas',
  intro_appetite: 'sinais_queixas',
  intro_digestion: 'sinais_queixas',
  intro_history: 'historico_clinico',
  intro_energy_history: 'historico_clinico',
  intro_lipedema_history: 'historico_clinico',
  intro_sleep_history: 'historico_clinico',
  intro_health: 'historico_clinico',
  intro_medication_surgery: 'historico_clinico',
  intro_routine: 'habitos_estilo_vida',
  intro_energy_lifestyle: 'habitos_estilo_vida',
  intro_haircare: 'habitos_estilo_vida',
  intro_lifestyle: 'habitos_estilo_vida',
  intro_lipedema_lifestyle: 'habitos_estilo_vida',
  intro_sleep_lifestyle: 'habitos_estilo_vida',
  intro_food: 'habitos_estilo_vida',
  intro_activity: 'habitos_estilo_vida',
  intro_sleep_stress: 'habitos_estilo_vida',
}

function dedupeSpecificSteps(steps: Step[]): Step[] {
  const seenByKey = new Map<string, number>()
  const deduped: Step[] = []

  for (const step of steps) {
    const dedupeKey = resolveDedupeKey(step)
    const existingIndex = seenByKey.get(dedupeKey)

    if (existingIndex === undefined) {
      seenByKey.set(dedupeKey, deduped.length)
      deduped.push(step)
      continue
    }

    const existingStep = deduped[existingIndex]
    if (shouldReplaceStep(existingStep, step)) {
      deduped[existingIndex] = step
    }
  }

  return deduped
}

function resolveDedupeKey(step: Step): string {
  if (step.mergeKey) return `merge:${step.mergeKey}`
  // Conditioned variants of the same inputKey (ex.: female/other)
  // should coexist unless they opt-in via mergeKey.
  if (step.inputKey && !step.condition) return `input:${step.inputKey}`
  return `id:${step.id}`
}

function shouldReplaceStep(current: Step, candidate: Step): boolean {
  const currentPriority = current.mergePriority ?? 0
  const candidatePriority = candidate.mergePriority ?? 0

  if (candidatePriority !== currentPriority) {
    return candidatePriority > currentPriority
  }

  // Tie-breaker: keep the richer choice schema.
  if (current.type === 'choice' && candidate.type === 'choice') {
    const currentOptionCount = current.options?.length ?? 0
    const candidateOptionCount = candidate.options?.length ?? 0
    return candidateOptionCount > currentOptionCount
  }

  return false
}

function isIntroStep(step: Step): boolean {
  return step.id.startsWith('intro_')
}

function resolveThemeForIntroId(stepId: string): ThemeKey {
  return introThemeMap[stepId] ?? 'complementares'
}

function buildThemeTransitionStep(theme: ThemeKey): Step {
  return {
    id: `theme_transition_${theme}`,
    type: 'message',
    content: themeTransitionMessages[theme],
  }
}

function groupStepsByTheme(steps: Step[]): Record<ThemeKey, Step[]> {
  const grouped: Record<ThemeKey, Step[]> = {
    metas_contexto: [],
    sinais_queixas: [],
    historico_clinico: [],
    habitos_estilo_vida: [],
    complementares: [],
  }

  let currentTheme: ThemeKey = 'metas_contexto'

  for (const step of steps) {
    if (isIntroStep(step)) {
      currentTheme = resolveThemeForIntroId(step.id)
    }
    grouped[currentTheme].push(step)
  }

  return grouped
}

export function buildFlowForObjectives(objetivos: Objetivo[]): Step[] {
  if (objetivos.length === 0) {
    return [...baseSteps, ...endSteps]
  }

  const objectivesWithSteps = objetivos.filter((objetivo) => (objectiveSteps[objetivo] ?? []).length > 0)

  // If only one objective contributes questions (e.g. genetics + one objective),
  // keep the original objective flow without themed transition wrappers.
  if (objectivesWithSteps.length <= 1) {
    const primaryObjective = objectivesWithSteps[0] ?? objetivos[0]
    const specific = objectiveSteps[primaryObjective] ?? []
    return [...baseSteps, ...specific, ...endSteps]
  }

  const specificSteps = objetivos.flatMap((objetivo) => objectiveSteps[objetivo] ?? [])
  const dedupedSpecific = dedupeSpecificSteps(specificSteps)
  const groupedSteps = groupStepsByTheme(dedupedSpecific)

  const themedSpecific: Step[] = []
  for (const theme of themeOrder) {
    const stepsForTheme = groupedSteps[theme]
    if (stepsForTheme.length === 0) continue
    const startsWithIntro = isIntroStep(stepsForTheme[0])
    if (!startsWithIntro) {
      themedSpecific.push(buildThemeTransitionStep(theme))
    }
    themedSpecific.push(...stepsForTheme)
  }

  return [...baseSteps, ...themedSpecific, ...endSteps]
}

/** Builds a complete flow for the given objective: base → specific → end */
export function buildFlow(objetivo: Objetivo): Step[] {
  return buildFlowForObjectives([objetivo])
}

/** Default flow (no objective selected) — just base + end */
export function defaultFlow(): Step[] {
  return [...baseSteps, ...endSteps]
}
